import size from 'lodash/size';
import omitBy from 'lodash/omitBy';
import template from 'string-placeholder';
import keys from 'lodash/keys';
import pick from 'lodash/pick';
import merge from 'lodash/merge';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import find from 'lodash/find';
import entries from 'lodash/entries';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import stripTags from 'striptags';
import { compareKeys, getOrFail } from 'mythil/object';
import values from 'lodash/values';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import forOwn from 'lodash/forOwn';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import identify from '~/identify';
import normalizeFieldValue from '~/data/normalizeFieldValue';
import { arrayed } from 'mythil/array';
import replaceall from 'replaceall';
import { deFormatDate, deFormatDateTime, parseGermanDate } from 'mythil/time';
import {
    getValueAdaptData,
    getValueAdaptSources,
    getAllResultParams,
    getFilters,
    getSelectedRows,
    getSelectThroughout,
} from '~/stateGetter';
import getConfig from '~/config';
import { fieldFile, timthumb } from '~/backend/backendUrl';
import { getInitialFieldValue, getRouter } from './stateGetter';
import { decode } from 'js-base64/base64';
import { getFilterType, isDateFilter } from './lib/filterTypes';

export const isFieldValueEqual = (v1, v2) => {
    return isEqual(v1.value, v2.value) && v2.min === v2.min && v2.max === v2.max;
};

// TODO: visibleconditions
export function presentFilters({ groupName, exceptGroupName, filters, parentSubview }) {
    let slice = filter(filters, (f) => {
        if (!f.visible || !f.editable) {
            return false;
        }
        if (groupName && f.group !== groupName) {
            return false;
        }
        if (exceptGroupName && f.group === groupName) {
            return false;
        }
        if (parentSubview && parentSubview.hiddenFields.indexOf(f.name) > -1) {
            return false;
        }

        return true;
    });

    if (groupName) {
        slice = filter(slice, (f) => f.group === groupName);
    }

    if (exceptGroupName) {
        slice = filter(slice, (f) => f.group !== exceptGroupName);
    }

    return orderBy(values(slice), 'order');
}

export function presentGroup(props) {
    let { group, groupOpen, filters } = props,
        presentedGroup = { ...group, open: groupOpen },
        groupName = presentedGroup.name;

    if (filters) {
        presentedGroup.filters = presentFilters({ groupName, ...props });
    }

    return presentedGroup;
}

export function presentGroups(props) {
    let { groupStates, groups, filters, onlyOpen } = props;

    filters = presentFilters(props);

    const filtersByGroups = groupBy(filters, 'group');
    return filters.reduce((filterGroups, filter) => {
        const defaultOpen = filter.group === 'default';
        const groupOpen = get(groupStates, filter.group);

        if (typeof groupOpen === 'boolean') {
            groupStates[filter.group] = filtersByGroups[filter.group].reduce(
                (state, filter) => Object.assign(state, { [filter.name]: groupOpen }),
                {}
            );
        }

        const filterOpen = get(groupOpen, filter.name, defaultOpen);
        // if(onlyOpen && !filterOpen) { return filterGroups; }

        filterGroups[filter.group] = filterGroups[filter.group] || {
            name: filter.group,
            title: filter.group,
            ...groups[filter.group],
            filters: [],
            open: Boolean(groupOpen),
        };

        filterGroups[filter.group].filters.push({ ...filter, open: filterOpen });

        return filterGroups;
    }, {});
}

export function normalizeFilterValue({ value, min, max }) {
    if (min) {
        return { min, max };
    }
    return value;
}

export function presentFilterValues({ filterValues, ...other }) {
    let subviewFilters = presentSubviewFilterValues(other);

    return {
        ...filterValues,
        ...subviewFilters,
    };
}

/**
 *
 * @param groupStates if specified only filterValues from open groups will be returned
 * @param filters filter definitions
 * @param filterValues all filter values
 * @param initialFilterValues if specified only filterValues that do not equal the default will be returned
 * @param parentItemValues for getting subview filter values
 * @param parentSubview for getting subview filter values
 * @returns {*}
 */

export function presentAppliedFilterOrder(props) {
    let { filterValues, filterOrder } = props;

    const asOrderedArr = (arr) =>
        orderBy(
            arr.map(([name, { created_at }]) => ({
                name,
                created_at: created_at ?? 0,
                order: get(filterOrder, name) ?? 0,
            })),
            ['created_at', 'order'],
            ['asc', 'desc']
        );

    return [
        ...asOrderedArr(entries(filterValues).filter(([_, { created_at }]) => created_at)),
        ...asOrderedArr(entries(filterValues).filter(([_, { created_at }]) => !created_at)),
    ].map(({ name }) => name);
}

export function presentAppliedFilterValues(props) {
    let { filters, filterValues, initialFilterValues, modelSearchFilter, takeAllCreatedFilters } = props;

    const isEmptyValue = (value) => isNil(value) || value === '';

    const normalizeValue = ({ value, filterType }) => {
        if (isDateFilter(filterType)) {
            return true;
        }

        if (isEmptyValue(value)) {
            return false;
        }

        if (Array.isArray(value)) {
            return !!value.length;
        }

        if (typeof value === 'object') {
            const isMinMax = 'min' in value || max in value;
            if (isMinMax && isEmptyValue(value.max) && isEmptyValue(value.min)) {
                return false;
            }
        }

        return true;
    };

    let appliedFilters = reduce(
        filters,
        (valCollector, filter, filterName) => {
            if (filterName === modelSearchFilter) {
                return valCollector;
            }

            let defaultFilterValue = get(initialFilterValues, filterName, undefined);
            let filterValue = get(filterValues, filterName, {});

            let takeValue = normalizeValue(filterValue) || (!!takeAllCreatedFilters && !!filterValue.created_at);

            if (typeof defaultFilterValue === 'undefined' || takeValue) {
                valCollector[filterName] = filterValue.value;
            }

            return valCollector;
        },
        {}
    );

    let subviewFilters = mapValues(presentSubviewFilterValues(props), normalizeFilterValue);

    return { ...appliedFilters, ...subviewFilters };
}

export function presentAppliedFilterValuesObject(props) {
    const appliedFilters = presentAppliedFilterValues(props);
    return mapValues(appliedFilters, (value, filterName) => {
        const filter = get(props.filterValues, filterName);
        return {
            filterType: filter?.filterType,
            created_at: filter?.created_at ?? new Date(filter?.created_at).getTime(),
            value,
        };
    });
}

export function canColumnBeVisible(columnName, { columns, parentSubview }) {
    let { visible, name, columnVisible } = columns[columnName];

    if (!visible || !columnVisible) {
        return false;
    }

    if (parentSubview) {
        if (parentSubview.hiddenColumns.indexOf(name) > -1) {
            return false;
        }
    }

    return true;
}

export function isColumnHidden(columnName, { columns, parentSubview, view, columnVisibility = null }) {
    if (columnVisibility && columnName in columnVisibility) {
        return !columnVisibility[columnName];
    }

    if (parentSubview && parentSubview.visibleColumns.length && (view === identify.VIEW_SUBVIEW || isItemView(view))) {
        return parentSubview.visibleColumns.indexOf(columnName) < 0;
    }

    return columns[columnName].hidden;
}

export function presentSortOptions(props) {
    let { sortOptions, isCalendarEnabled, calendarView } = props;

    if (isCalendarEnabled && calendarView) {
        return {
            direction: 'asc',
            field: calendarView.from_column,
        };
    }

    return sortOptions;
}

export function presentColumns(props) {
    let { columns, columnOrder, isCalendarEnabled, calendarView } = props;

    if (isCalendarEnabled && calendarView) {
        return [{ name: calendarView.from_column }, { name: calendarView.to_column }];
    }

    columns = values(columns).reduce((currentColumns, column) => {
        let { name } = column;
        if (!canColumnBeVisible(name, props)) {
            return currentColumns;
        }

        currentColumns.push({
            ...column,

            // offset default index by 1 to allow moving columns to first position.
            displayOrder: +get(columnOrder, column.name, currentColumns.length + 1),
            tooltip: props.tooltipsActive ? column.tooltip : null,
            hidden: isColumnHidden(name, props),
        });

        return currentColumns;
    }, []);

    return sortBy(columns, (column) => column.displayOrder);
}

export function presentFieldOption(field, value, autoCompleteOptions) {
    let options = field.options;

    if (field.isAutocomplete) {
        options = get(autoCompleteOptions, `${field.name}.autocomplete.value`, []);
    }

    let option = find(options, { value });

    return option ? option : {};
}

export function presentConditionSources(visibilityConditions) {
    let valueKeys = [],
        optionKeys = [];

    forOwn(visibilityConditions, (condition, field) => {
        if (!isString(condition)) {
            return;
        }

        valueKeys.push(field);
        optionKeys.push(field);

        let regex = /\.(.*?)(\s|$|\))/g,
            match;

        do {
            match = regex.exec(condition);
            if (match) {
                valueKeys.push(match[1]);
            }
        } while (match);
    });

    return {
        valueKeys: valueKeys,
        optionKeys: optionKeys,
    };
}

export function presentFieldConditionSources(fields) {
    let valueKeys = [],
        optionKeys = [];

    forOwn(fields, (field) => {
        let conditions = field.visibleConditions;
        let conditionSources = presentConditionSources(conditions);

        valueKeys = [...valueKeys, ...conditionSources.valueKeys];
        optionKeys = [...optionKeys, ...conditionSources.optionKeys];
    });

    return {
        valueKeys: uniq(valueKeys),
        optionKeys: uniq(optionKeys),
    };
}

export function presentPrimaryKey({ values, primaryKeyAttr = 'id', defaultKey = null }) {
    let key = get(values, `${primaryKeyAttr}.value`, get(values, primaryKeyAttr));

    if (!key) {
        return defaultKey;
    }

    if (isString(key)) {
        return key.length ? key : defaultKey;
    }

    return key;
}

// TODO: use mini-javascript parser and not eval for security reasons, possible XSS
export function evalVisibleConditions({ field, editFieldValues, editFieldOptions, itemId, view, ...other }) {
    let visibleConditions = field.visibleConditions;

    for (let sourceFieldName in visibleConditions) {
        if (!visibleConditions.hasOwnProperty(sourceFieldName)) continue;

        let condition = visibleConditions[sourceFieldName];

        if (!condition) {
            continue;
        }

        let value = editFieldValues[sourceFieldName],
            options = find(editFieldOptions[sourceFieldName], { value }),
            creating = itemId === identify.NEW_ITEM;

        try {
            let result = (function (values, value, options, creating, view) {
                return !!eval(condition);
            })(editFieldValues, value, options, creating, view);

            // console.log('visibleCondition '+field.name+' ', { values, value, options, creating });
            // console.log('visibleCondition '+field.name+' | '+sourceFieldName+' "'+condition+'" => ', result);

            if (!result) {
                return false;
            }
        } catch (err) {
            console.error('Error while evaluating condition for ' + sourceFieldName + ' "' + condition + '"', err);
            return false;
        }
    }

    return true;
}

export function prepareValueForSave(field, value) {
    if (!value) {
        return false;
    }

    const prepareSingleValue = (value) => {
        switch (field.type) {
            case 'date':
                try {
                    if (!value) {
                        return null;
                    }
                    value = replaceall('_', '', value);

                    return deFormatDate(parseGermanDate(value));
                } catch (e) {
                    return null;
                }
            case 'datetime':
                try {
                    if (!value) {
                        return null;
                    }

                    value = replaceall('_', '', value);

                    return deFormatDateTime(parseGermanDate(value));
                } catch (e) {
                    return null;
                }
        }

        return value;
    };

    if (typeof value === 'object') {
        if ('min' in value) {
            return {
                min: prepareSingleValue(value.min),
                max: prepareSingleValue(value.max),
            };
        }

        if ('value' in value) {
            return prepareSingleValue(value.value);
        }
    }

    return prepareSingleValue(value);
}

export function presentFilterValuesForQuery(filters, values) {
    return mapValues(values, (value, name) => {
        let filter = filters[name];
        return prepareValueForSave(filter, value);
    });
}

export function isModelViewValid(view) {
    return view && view !== identify.VIEW_SUBVIEW;
}

export function isItemView(view) {
    return view === identify.VIEW_VIEW || view === identify.VIEW_EDIT;
}

export function isTabularView(view) {
    return view === identify.VIEW_TABULAR;
}

export function presentColumnFields({ columns, itemRow }) {
    return reduce(
        columns,
        (columnFields, column) => {
            if (column.visible && column.fieldGroup) {
                columnFields[column.name] = {
                    name: 'field_' + column.name,
                    label: column.title,
                    value: itemRow[column.name].rendered,
                    group: column.fieldGroup,
                    visible: true,
                    tooltip: column.tooltip,
                    order: column.order,
                    renderAsHtml: true,
                    interval: column.interval,
                    timerDueText: column.timerDueText,
                };

                return columnFields;
            }

            return columnFields;
        },
        {}
    );
}

export function presentVisibleEditFields(
    props,
    checks = { visible: true, nonew: true, subview: true, condition: true, onlyPersisted: false }
) {
    let { editFields } = props;
    let fieldsToAdapt = pick(props.editFields, keys(props.editFieldValues));

    props = { ...props, ...normalizeValueDependencies(props) };
    props.editFieldValues = prepareFieldValues(fieldsToAdapt, props);
    props.editFieldValues = mapValues(props.editFieldValues, (val, fieldName) => {
        return prepareValueForSave(editFields[fieldName], val);
    });

    // inject those values into the edit field for future use (for example saving)
    editFields = mapValues(props.editFields, (editField, fieldName) => {
        return {
            ...editField,
            value: props.editFieldValues[fieldName],
        };
    });

    let visibleEditFields = reduce(
        editFields,
        (currentVisibleFields, field, fieldName) => {
            if (
                (checks.visible && !field.visible) ||
                (checks.nonew && props.itemId === identify.NEW_ITEM && !field.editable) ||
                (checks.subview && props.parentSubview && props.parentSubview.hiddenFields.indexOf(fieldName) > -1) ||
                (checks.condition && !evalVisibleConditions({ field, ...props })) ||
                (checks.onlyPersisted && field.notPersisted)
            ) {
                return currentVisibleFields;
            }

            currentVisibleFields[fieldName] = field;
            return currentVisibleFields;
        },
        {}
    );

    return visibleEditFields;
}

export function onlyPersisted(fieldDatas, editFields) {
    editFields = editFields ? editFields : fieldDatas;

    return reduce(
        fieldDatas,
        (theFieldDatas, fieldData, fieldName) => {
            if (editFields[fieldName].notPersisted) {
                return theFieldDatas;
            }

            theFieldDatas[fieldName] = fieldDatas[fieldName];
            return theFieldDatas;
        },
        {}
    );
}

export function dropIgnoredFields(editFields) {
    return {
        editFields: reduce(
            editFields,
            (fields, field, fieldName) => {
                if (field.ignored) {
                    return fields;
                }

                fields[fieldName] = editFields[fieldName];
                return fields;
            },
            {}
        ),
    };
}

export function presentSaveEditFields(props) {
    return presentVisibleEditFields(props, { visible: false, nonew: false, subview: false, condition: true });
}

export function presentElements(elementTypes) {
    return mapValues(elementTypes, (elementType) => {
        return groupBy(elementType, 'group');
    });
}

export function normalizeAllFieldOptions(fields, allOptions) {
    try {
        return reduce(
            allOptions,
            (theOptions, options, name) => {
                let field = fields[name];

                theOptions[name] = field.isAutocomplete ? getOrFail(options, `autocomplete.value`) : field.options;

                return theOptions;
            },
            {}
        );
    } catch (err) {
        throw err;
    }
}

export function mergeField(props) {
    let { field, value, initialValue } = props;

    let fieldData = {
        ...field,
        initialValue,
    };

    if (!field.adaptValue) {
        return { ...fieldData, value };
    }

    props = { ...props, ...normalizeValueDependencies(props) };
    value = presentFieldValue(field, props);

    return { ...fieldData, value };
}

export function presentConstraintValues(inputType, constraints, props) {
    props = {
        ...props,
        ...normalizeValueDependencies(props),
    };

    let fields = inputType === identify.EDITFIELD ? props.editFields : props.filters;

    return mapValues(constraints, (v, fieldName) => {
        return presentFieldValue(fields[fieldName], props).value;
    });
}

export function presentItemTitle({ itemId, singleTitle, modelTitle, resultsValue, titleColumn }) {
    if (!itemId) {
        return modelTitle;
    }
    if (itemId === identify.NEW_ITEM) {
        return '+ ' + singleTitle;
    }

    let row = mapValues(get(resultsValue, `results.${itemId}`), (col) => col.rendered);

    if (!row || size(row) < 1) {
        return singleTitle;
    }

    if (!titleColumn || !titleColumn.length) {
        return get(row, 'title', singleTitle);
    }

    return template(titleColumn, row);
}

export function presentRawTitle(props) {
    return stripTags(presentItemTitle(props));
}

export function areValuesEqual(a, b) {
    const compareFoo = (a, b, key) => {
        let isEqual = isFieldValueEqual(a, b);
        return isEqual;
    };

    return compareKeys(a, b, { compareFoo });
}

export function presentRouterRoute(routerInfo) {
    return {
        pathname: routerInfo.pathname,
        query: routerInfo.query,
        options: routerInfo.options,
    };
}

export function presentActionInfo({
    groups,
    selectedRows,
    actions,
    actionResult = null,
    executeAction = null,
    executeScanQrAction = null,
}) {
    actions = actions.map((theAction) => {
        const groupName = theAction.group;
        return {
            ...theAction,
            action: theAction.scanQr ? executeScanQrAction : executeAction,
            groupName: groupName,
            group: get(groups, `${groupName}`),
            isActive: actionResult
                ? actionResult.actionName === theAction.value && actionResult.status === identify.STATUS_STARTED
                : false,
            requiresSelection: theAction.requires_selection && selectedRows.length === 0,
            criticalConfirmQuantity:
                theAction.criticalConfirmQuantity !== true && theAction.criticalConfirmQuantity
                    ? theAction.criticalConfirmQuantity
                    : 0,
            criticalConfirmText:
                theAction.criticalConfirmText ||
                (theAction.criticalConfirmQuantity ? theAction.caption.toUpperCase().replace(/_/g, ' ') : ''),
            disabled: !theAction.hasPermission,
        };
    });

    const activeAction = actionResult ? find(actions, { isActive: true }) : null,
        activeGroup = activeAction?.group,
        isActionActive = !!activeAction;

    return { actions, activeAction, activeGroup, isActionActive };
}

export function presentItemActionInfo({
    groups,
    actions,
    itemId,
    saveResult,
    deleteResult,
    deleteItem = null,
    openItemLink = null,
    executeAction = null,
    executeScanQrAction = null,
    actionPermissions = {},
    trans,
    itemTitle,
    deleteTooltip,
    selectedRows = [],
    resultsValue,
}) {
    actions = presentActionInfo({ groups, actions, executeAction, executeScanQrAction, selectedRows }).actions;

    let saving = saveResult.status === identify.STATUS_STARTED,
        deleting = deleteResult.status === identify.STATUS_STARTED;

    if (itemId !== identify.NEW_ITEM) {
        actions.push({
            // icon: 'delete',
            groupName: '',
            value: '_delete',
            icon: 'delete-forever',
            caption: trans('remove'),
            confirmation: true,
            isActive: false,
            disabled: !get(actionPermissions, 'delete', false),
            action: deleteItem,
            tooltip: deleteTooltip,
        });
    }

    return {
        saving,
        deleting,
        actions,
    };
}

export function presentAllResultParams(props) {
    // console.log('presentAllResultParams', props);

    let resultParams = {
        modelName: props.modelName,
        filters: presentAppliedFilterValues(props),
        page: props.page || 1,
        sortOptions: presentSortOptions(props),
        perPage: props.isCalendarEnabled ? props.perPage : Math.min(props.perPage, getConfig().maxPerPage),
        columns: presentColumns(props)
            .filter((c) => !c.hidden)
            .map(({ name }) => name)
            .sort()
            .join(','),
    };

    return resultParams;
}

export function presentAuthError(authResult) {
    // TODO?

    if (authResult.value) {
        return false;
    }
    if (authResult.started_at && authResult.value === null) {
        return false;
    }
    return authResult.error;
}

export function presentSubviewFilterValues({ parentPrimaryKey, parentItemId, parentItemValues, parentSubview }) {
    if (!parentSubview || !parentItemValues || !parentItemId) {
        return {};
    }

    let filterValue = null;
    if (parentSubview.filterValue === parentPrimaryKey) {
        filterValue = { value: parentItemId };
    } else {
        filterValue = parentItemValues[parentSubview.filterValue];
    }

    return { [parentSubview.filterName]: filterValue };
}

export function presentResultDependenciesLoaded(props) {
    let { subviewName, parentSubview, parentItemState, parentItemId, parentPrimaryKey } = props;
    if (!subviewName || !subviewName.length) {
        return true;
    }

    if (parentItemId === identify.NEW_ITEM || !parentItemId) {
        return false;
    }

    if (parentSubview.filterValue === parentPrimaryKey) {
        return true;
    }

    if (!parentItemState.value.itemId) {
        return false;
    }

    return parentItemState.value.itemId === parentItemId;
}

export function presentResultsLoading(props) {
    if (!presentResultDependenciesLoaded(props)) {
        return true;
    }

    let { resultState } = props,
        loading = resultState.status === identify.STATUS_STARTED;

    if (loading) {
        return true;
    }

    let resultParams = presentAllResultParams(props),
        loadedResultParams = get(resultState, 'loadedParams');

    let parametersNotEqual = !isEqual(resultParams, loadedResultParams);

    return parametersNotEqual;
}

export function presentSubviewIdentifier({ subview, subviews }) {
    let count = reduce(
        subviews,
        (theCount, subviewCandidate) => {
            return subviewCandidate.model === subview.modelName ? theCount + 1 : theCount;
        },
        0
    );

    return count > 1 ? subview.name : subview.model;
}

export function presentModelIdentifier({
    loadedModels,
    currentModelId,
    subviews,
    includeItemId = false,
    includeMode = false,
}) {
    let slug = '';

    let currentModel = get(loadedModels, currentModelId);
    if (!currentModel.subviewName) {
        slug = `${slug}/${currentModel.modelName}`;
    } else {
        slug = `${slug}/${presentSubviewIdentifier({
            subview: getOrFail(subviews, currentModel.subviewName),
            subviews,
        })}`;
    }

    if (includeItemId && currentModel.item.id) {
        slug = `${slug}/${currentModel.item.id}`;
    }

    if (includeMode && currentModel.view === identify.VIEW_EDIT) {
        slug = `${slug}/edit`;
    }

    return slug;
}

export function presentModelUrl({
    loadedModels,
    modelId,
    models,
    includeCurrent = false,
    includeMode = false,
    includeItemId = false,
}) {
    let nameSlug = '';
    let initialModelId = modelId;

    while (modelId) {
        let currentModel = get(loadedModels, modelId),
            currentModelId = modelId;

        modelId = currentModel.parentModelId;

        // TODO idk, this is wrong somehow, rename one thing means happy refactoring
        let modelName = get(loadedModels, `${modelId}.modelName`, null);
        let subviews = get(models, `${modelName}.dataModel.item.subviews`, {});

        let modelIdentifier = presentModelIdentifier({ loadedModels, currentModelId, subviews, includeItemId: true });
        if (currentModelId === initialModelId) {
            modelIdentifier = presentModelIdentifier({
                loadedModels,
                currentModelId,
                subviews,
                includeMode,
                includeItemId,
            });
        }

        if (currentModelId !== initialModelId || includeCurrent) {
            nameSlug = nameSlug.length ? `${modelIdentifier}${nameSlug}` : modelIdentifier;
        }
    }

    return nameSlug;
}

export function normalizeValueDependencies({ emptyFilterValues, ...props }) {
    return {
        filterValues: merge({}, emptyFilterValues, presentFilterValues(props)),
        filterOptions: normalizeAllFieldOptions(props.filters, props.filterOptions),
        editFieldOptions: normalizeAllFieldOptions(props.editFields, props.editFieldOptions),
    };
}

// TODO: support min/max for coverage even if I dont know how one can use that
// TODO: support for multiselect, needs isEqual function
export function presentFieldValue(field, props) {
    try {
        let {
            editFields,
            filters,
            initialEditFieldValues,
            editFieldValues,
            filterValues,
            initialFilterValues,
            filterOptions,
            editFieldOptions,
        } = props;

        let adaptInfo = field.adaptValue;

        let fieldInputType = field.inputType,
            fieldValueInfo =
                fieldInputType === identify.EDITFIELD ? editFieldValues[field.name] : filterValues[field.name],
            initialFieldValueInfo =
                fieldInputType === identify.EDITFIELD
                    ? initialEditFieldValues[field.name]
                    : initialFilterValues[field.name];

        if (!adaptInfo) {
            return fieldValueInfo;
        }

        // console.log('ADAPTING '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value);

        let sourceField =
            adaptInfo.inputType === identify.EDITFIELD ? editFields[adaptInfo.field] : filters[adaptInfo.field];

        if (!sourceField) {
            return fieldValueInfo;
        }

        // recursively resolve source value
        let sourceValueInfo = presentFieldValue(sourceField, props);

        const getAdaptValue = (valuePath = 'value') => {
            let value = sourceValueInfo[valuePath];

            if (!adaptInfo.option) {
                return normalizeFieldValue(field.inputType, field.type, value);
            }
            let allFieldOptions =
                    adaptInfo.inputType === identify.EDITFIELD
                        ? editFieldOptions[sourceField.name]
                        : filterOptions[sourceField.name],
                options = find(allFieldOptions, { value });

            // as we do not know the format of the option, we need to format it according to the fields expectation
            return normalizeFieldValue(
                field.inputType,
                field.type,
                get(options, adaptInfo.option, initialFieldValueInfo.value)
            );
        };

        // if user input in that field is more recent than a change in the source input the user input takes precedence
        if (fieldValueInfo.changed_at > sourceValueInfo.changed_at) {
            return fieldValueInfo;
        }

        // if source was changed more recently and mode is overwrite we want the source value
        if (adaptInfo.mode === identify.VALUE_ADAPT_OVERWRITE) {
            // console.log('ADAPT overriding '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // we assume that we are in substitution mode from now on

        // if the field still has the initial value we want the source value
        if (initialFieldValueInfo.value === fieldValueInfo.value) {
            // console.log('ADAPT substitute if the field still has the initial value we want the source value '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // if the field is equal to the previous adapted value we want the source value as well
        if (fieldValueInfo.value === getAdaptValue('previousValue')) {
            // console.log('ADAPT substitute if the field is equal to the previous adapted value we want the source value as well '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

            return { ...fieldValueInfo, value: getAdaptValue() };
        }

        // console.log('ADAPT original value '+field.inputType+' '+field.name+' value to value from '+adaptInfo.inputType+' '+adaptInfo.field+' '+fieldValueInfo.value+' => '+getAdaptValue());

        // otherwise return the original field value
        return fieldValueInfo;
    } catch (err) {
        throw err;
    }
}

export function prepareFieldValues(fieldsToPrepare, props) {
    return mapValues(fieldsToPrepare, (field) => presentFieldValue(field, props));
}

export const prepareComparableFieldValues = (fieldsToPrepare, props) => {
    return mapValues(fieldsToPrepare, (field) => {
        const preparedField = presentFieldValue(field, props);

        if (!Array.isArray(preparedField.value)) return preparedField;
        if (field.orderable) return preparedField;
        return {
            ...preparedField,
            value: sortBy(preparedField.value),
        };
    });
};

// TODO is this necceccarry? right now not used, fix constraints, bookings errors due to no values
export function getSourceFieldNames(fields, editFields, filters, { collectNames = true, makeUniq = true }) {
    fields = arrayed(fields);

    let sourceFieldNames = {
        [identify.EDITFIELD]: [],
        [identify.FILTERFIELD]: [],
    };

    let typedSourceFields = {
        [identify.EDITFIELD]: {},
        [identify.FILTERFIELD]: {},
    };

    const prepareSourceFieldNames = () => {
        let preparedSourceFieldnames = sourceFieldNames;

        if (makeUniq) {
            preparedSourceFieldnames = {
                [identify.EDITFIELD]: uniq(sourceFieldNames[identify.EDITFIELD]),
                [identify.FILTERFIELD]: uniq(sourceFieldNames[identify.FILTERFIELD]),
            };
        }

        return preparedSourceFieldnames;
    };

    forOwn(fields, (field) => {
        if (collectNames) {
            sourceFieldNames[field.inputType].push(field.name);
        }

        if (field.constraints) {
            let fields = field.inputType === identify.EDITFIELD ? editFields : filters;

            for (let key in field.constraints) {
                if (!field.constraints.hasOwnProperty(key)) {
                    continue;
                }

                let sourceField = fields[key];
                if (!sourceField) {
                    continue;
                }

                typedSourceFields[field.inputType][key] = sourceField;
            }
        }

        if (field.adaptValue) {
            let sourceField =
                field.adaptValue.inputType === identify.EDITFIELD
                    ? editFields[field.adaptValue.field]
                    : filters[field.adaptValue.field];

            if (sourceField) {
                typedSourceFields[sourceField.inputType][sourceField.name] = sourceField;
            }
        }
    });

    let sourceFields = [
        ...values(typedSourceFields[identify.EDITFIELD]),
        ...values(typedSourceFields[identify.FILTERFIELD]),
    ];
    if (!sourceFields.length) {
        return prepareSourceFieldNames(sourceFieldNames);
    }

    let nestedSourceNames = getSourceFieldNames(sourceFields, editFields, filters, {
        collectNames: true,
        makeUniq: false,
    });

    sourceFieldNames = {
        [identify.EDITFIELD]: [...sourceFieldNames[identify.EDITFIELD], ...nestedSourceNames[identify.EDITFIELD]],
        [identify.FILTERFIELD]: [...sourceFieldNames[identify.FILTERFIELD], ...nestedSourceNames[identify.FILTERFIELD]],
    };

    return prepareSourceFieldNames(sourceFieldNames);
}

// export function reduceFieldSources(fields, editFields, filters, sources) {
//
// }

export function reduceFieldSources(fields, editFields, filters, sources) {
    let sourceFieldNames = getSourceFieldNames(fields, editFields, filters, sources);

    let editFieldSources = mapValues(
        pick(sources, ['initialEditFieldValues', 'editFieldValues', 'editFieldOptions']),
        (source) => {
            return pick(source, sourceFieldNames[identify.EDITFIELD]);
        }
    );

    let filterSources = mapValues(
        pick(sources, ['filterValues', 'initialFilterValues', 'emptyFilterValues', 'filterOptions']),
        (source) => {
            return pick(source, sourceFieldNames[identify.FILTERFIELD]);
        }
    );

    return { ...editFieldSources, ...filterSources };
}

export function presentSimpleEntry({ entry, resultInfo }) {
    return {
        title: stripTags(get(entry, `${resultInfo.titleColumn}.rendered`, null)),
        body: stripTags(get(entry, `${resultInfo.bodyColumn}.rendered`, null)),
        info: stripTags(get(entry, `${resultInfo.infoColumn}.rendered`, null)),
    };
}

export function presentSimpleResults({ results, resultInfo }) {
    return mapValues(results, (entry) => {
        return presentSimpleEntry({ entry, resultInfo });
    });
}

export function presentUpdatedValues({ startValues, values }) {
    return reduce(
        values,
        (theUpdatedValues, value, name) => {
            let startValue = startValues[name];

            if (isFieldValueEqual(startValue, value)) {
                return theUpdatedValues;
            }

            theUpdatedValues[name] = value;

            return theUpdatedValues;
        },
        {}
    );
}

export function haveValuesChanged(props) {
    let updatedValues = presentUpdatedValues(props);

    let changedValueCount = size(updatedValues);

    return changedValueCount > 0;
}

export function getSaveValues(state, modelId) {
    let props = {
            ...getValueAdaptData(state, modelId),
            ...getValueAdaptSources(state, modelId),
        },
        visibleEditFields = presentSaveEditFields(props);

    return mapValues(visibleEditFields, 'value');
}

export function getResultQueryParams(state, modelId) {
    let rawResultParams = getAllResultParams(state, modelId),
        filters = getFilters(state, modelId),
        resultParams = presentAllResultParams(rawResultParams);

    return {
        ...resultParams,
        filters: presentFilterValuesForQuery(filters, resultParams.filters),
    };
}

export function getResultQueryParamsWithSelectedRows(state, modelId) {
    const queryParams = getResultQueryParams(state, modelId);
    const ids = getSelectedRows(state, modelId);
    const selectThroughout = getSelectThroughout(state, modelId);
    return {
        ...queryParams,
        filters: {
            ...queryParams.filters,
            _ids: !selectThroughout && ids.length ? ids : undefined,
            _without_ids: selectThroughout && ids.length ? ids : undefined,
        },
    };
}

export function getModelsWithChangedData(state) {
    let loadedModels = state.ui.loadedModels;

    return reduce(
        loadedModels,
        (um, model, modelName) => {
            if (!get(model, 'item.id') || !get(model, 'itemData.finished_at')) {
                return um;
            }

            let modelId = model.modelId;

            let valueAdaptProps = {
                ...getValueAdaptData(state, modelId),
                ...getValueAdaptSources(state, modelId),
            };

            valueAdaptProps = { ...valueAdaptProps, ...normalizeValueDependencies(valueAdaptProps) };
            valueAdaptProps = { ...valueAdaptProps, ...dropIgnoredFields(onlyPersisted(valueAdaptProps.editFields)) };

            let itemData = model.itemData.value;
            let values = prepareComparableFieldValues(valueAdaptProps.editFields, valueAdaptProps),
                startValues = prepareComparableFieldValues(valueAdaptProps.editFields, {
                    ...valueAdaptProps,
                    initialEditFieldValues: itemData.initialValues,
                    editFieldValues: itemData.values,
                });

            let updatedValues = presentUpdatedValues({ values, startValues });

            if (size(updatedValues) > 0) {
                um[modelName] = {
                    updatedValues,
                    model,
                };
            }

            return um;
        },
        {}
    );
}

export function presentHelpTitle(entry, defaultStr) {
    return entry ? entry.title : defaultStr;
}

export function presentSearchResultParams({ searchModel, searchTerm, searchSort, searchFilter }) {
    try {
        let resultParams = {
            filters: {
                [searchFilter]: searchTerm,
            },
            modelName: searchModel.modelName,
            page: searchModel.results.value.page || 1,
            sortOptions: searchSort,
            perPage: searchModel.resultParams.perPage,
        };

        return resultParams;
    } catch (err) {
        console.log('yeah');
    }
}

export function haveSearchModelsErrored(searchModels) {
    let findResult = find(searchModels, (model) => {
        return model.results.status === identify.STATUS_ERRORED;
    });

    return !!findResult;
}

export function presentFieldFile({ publicPath, name, modelName, itemId = null, value = null }) {
    let baseUrl = getConfig().basePublicUrl;

    if (publicPath) {
        return `${baseUrl}/${publicPath}/${value}`;
    }

    return fieldFile(modelName, itemId, name);
}

export function presentFieldImage(props, resizeProps) {
    let { publicPath, name, modelName, itemId = null, value = null } = props;

    if (publicPath) {
        return timthumb(`${publicPath}/${value}`, resizeProps);
    }

    return fieldFile(modelName, itemId, name);
}

export function deriveNewListOrder(list, oldIndex, newIndex) {
    const columnOrder = list.map(({ displayOrder }) => displayOrder);
    const [leftOrder = 0, newOrder = leftOrder + 1, rightOrder = newOrder + 1] = [
        columnOrder[newIndex - 1],
        columnOrder[newIndex],
        columnOrder[newIndex + 1],
    ];

    const column = list[oldIndex];
    let orderDifference =
        oldIndex > newIndex
            ? // when moving down, subtract difference from newOrder
              (leftOrder - newOrder) / 2
            : // when moving up, add difference to newOrder
              (rightOrder - newOrder) / 2;

    return { [column.name]: newOrder + orderDifference };
}

export function presentRouteViews(state) {
    const base64View = getRouter(state).params.viewId;
    if (!base64View) return null;

    try {
        return JSON.parse(decode(base64View));
    } catch (e) {
        return null;
    }
}

export function presentFilteredViewsWithRelativeDates(view) {
    return omitBy(
        {
            ...view,
            filters:
                view.filters &&
                mapValues(view.filters, ({ value, ...filter }) => ({
                    ...filter,
                    value: !isDateFilter(filter.filterType)
                        ? value
                        : getFilterType('date')
                              .find(({ name }) => name === filter.filterType)
                              .getValue(),
                })),
        },
        (x) => x === undefined
    );
}
